import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { App } from '@df/core/app';
import { NICmsContentField } from '@icms/core/content';
import { ICmsPageType } from '@icms/core/content/page/icms-page-type.decorator';
import { PageFooterVisibilityService } from 'projects/app-default/src/app/layout/page/footer/page-footer-visibility.service';
import { NBuilderConfig } from '../common/builder.config';
import { PageBuilderhViewComponentDef } from '../common/page-builder-view.component.def';
import { NBuilderProductsSetup } from '../common/products-setup/builder-products-setup.config';
import { IStepSelection } from '../common/simple-step/simple-step.component';
import { KitGeneralSetupComponent } from './general-setup/kit-general-setup.component';
import { NKitGeneralSetup } from './general-setup/kit-general-setup.config';
import { KitBuilderService } from './kit-builder.service';
import { KitProductsSetupComponent } from './product-setup/kit-products-setup.component';
export namespace NKitBuilder {
    export const typeId = 'kit-builder';
    export enum EConfigureOptions {
        toy = 'Toy',
        rider = 'Rider'
    }
    export enum ESteps {
        pony,
        products
    }
    export interface IPageData extends NKitGeneralSetup.IGeneralSetupData, NBuilderProductsSetup.IProductsSetupData {
        title: string;
        displayNewLabelForToggle: boolean;
        leftSectionPony: NBuilderProductsSetup.ILeftSectionElement[];
        leftSectionRider: NBuilderProductsSetup.ILeftSectionElement[];
        riderShift: number;
    }
    export interface IStorageData extends NBuilderConfig.IStorageData {
        step: ESteps;
        pony?: IStepSelection;
        riderViewVisited?: boolean;
    }
}
@ICmsPageType({
    id: NKitBuilder.typeId,
    title: 'Kit Builder',
    fields: [
        ...NKitGeneralSetup.generalSetupFields,
        ...NBuilderProductsSetup.productsSetupFields,
        {
            key: 'typeData.displayNewLabelForToggle',
            tab: 'Builder General Setup',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Display new label for toggle',
            default: false
        },
        <NICmsContentField.IRepeaterField>{
            key: 'typeData.leftSectionPony',
            tab: 'Builder General Setup',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Left section',
            itemName: 'Pony option',
            itemNameField: 'hintLabel',
            required: true,
            defaultSize: 1,
            minSize: 1,
            fields: [...NBuilderProductsSetup.leftSectionElementFields, ...NBuilderProductsSetup.focusPositionFields]
        },
        <NICmsContentField.INumberField>{
            tab: 'Builder Rider Setup',
            key: 'typeData.riderShift',
            type: NICmsContentField.EFieldType.number,
            title: 'Rider image position',
            description: 'Shift rider image horizontally (from -40 to 40)',
            required: true,
            min: -40,
            max: 40,
            default: 0
        },
        <NICmsContentField.IRepeaterField>{
            tab: 'Builder Rider Setup',
            key: 'typeData.leftSectionRider',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Left section rider',
            itemName: 'Rider option',
            itemNameField: 'listingTitle',
            required: true,
            defaultSize: 1,
            minSize: 1,
            fields: [
                ...NBuilderProductsSetup.leftSectionElementFields,
                {
                    key: 'mainOption',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Main option',
                    description: 'Preselect first simple on toggle change, on remove delete all related options',
                    default: false
                },
                ...NBuilderProductsSetup.focusPositionFields
            ]
        }
    ]
})
@Component({
    selector: 'page-kit-builder-view',
    templateUrl: 'page-kit-builder-view.html',
    styleUrls: ['./page-kit-builder-view.scss'],
    standalone: true,
    imports: [KitGeneralSetupComponent, KitProductsSetupComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageKitBuilderViewComponent extends PageBuilderhViewComponentDef implements OnInit, OnDestroy {
    protected storageData: NKitBuilder.IStorageData = { step: 0 };
    protected readonly ESteps = NKitBuilder.ESteps;
    protected isFullLoaded = false;

    constructor(
        pageFooterVisibilityService: PageFooterVisibilityService,
        hashStorageService: HashStorageService,
        app: App,
        private kitBuilderService: KitBuilderService
    ) {
        super(pageFooterVisibilityService, hashStorageService, app);
    }

    protected async loadMainProducts() {
        const sectionProducts = (await this.kitBuilderService.loadSectionProducts(this.typeData.firstStepOptions)).filter(product =>
            product.isSalable()
        );
        if (!sectionProducts?.length) {
            console.warn('There are no salables products in pony options');
        }
        this.isLoaded = !!sectionProducts.length;
        const allProducts = await this.kitBuilderService.loadAllProducts(this.typeData.useToyAttr);
        if (!allProducts?.length) {
            const attrName = this.typeData.useToyAttr ? 'toy_eligible_builder' : 'eligible_builder';
            console.warn(
                `There are no product for kit builder. Attr products with '${attrName}' and correct outfit_builder_section are needed`
            );
        }

        // read state after loading main products cause we need products to validate data
        this.readState();

        // load selected children products before display gallery
        if (this.storageData.selection) {
            const ids = Object.values(this.storageData.selection)
                ?.map(option => option.matchedProductId)
                ?.filter(id => !!id);
            if (ids?.length) {
                await this.app.resources.product.loadAllIds(ids, 2, { safe: true });
            }
        }

        this.isFullLoaded = this.isLoaded && !!allProducts?.length;
    }
    protected validateData(data: NKitBuilder.IStorageData) {
        return this.kitBuilderService.validateData(data, this.typeData.firstStepOptions);
    }
}
